import React, { useEffect } from 'react';
import { Container, Row, Col } from 'react-bootstrap';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import ImageDialogTrigger from '../components/ImageDialogTrigger';
import ProjectBreadcrumbs from '../components/ProjectBreadcrumbs';
import semanaEdicoes from '../data/semanaEdicoes';
import resolvePublicAsset from '../utils/resolvePublicAsset';

function SemanaHipHop() {
  const location = useLocation();
  const navigate = useNavigate();

  const historiaTimeline = [
    {
      periodo: '2011',
      titulo: 'Origem e resistência',
      descricao: 'A primeira edição da Semana do Hip Hop aconteceu em 2011, idealizada por Renato Magu, que utilizou recursos do próprio PIS para viabilizar o evento. Sem patrocínio e com pouca estrutura, a realização só foi possível graças à mobilização de voluntários e à força da comunidade, que acreditou na importância de criar um espaço dedicado à cultura Hip Hop na cidade. Foi um momento marcado pela disposição, resistência e espírito coletivo.',
      imagemPlaceholder: 'Show de encerramento da primeira edição da Semana do Hip Hop de Bauru com SNJ',
      image: resolvePublicAsset('/images/semanahiphop/2011/show_encerramento_snj.jpg')
    },
    {
      periodo: '2013',
      titulo: 'Reconhecimento institucional',
      descricao: 'Após anos de mobilização, pressão popular e articulação política, em 2013 a Semana do Hip Hop conquistou um marco importante: tornou-se lei municipal. A partir desse momento, o evento passou a integrar oficialmente o calendário de Bauru, garantindo maior visibilidade e reconhecimento, além de consolidar sua importância como política pública cultural.',
      imagemPlaceholder: 'Comemoração da aprovação da lei municipal que instituiu a Semana do Hip Hop de Bauru',
      image: resolvePublicAsset('/images/semanahiphop/2013/lei_municipal.jpg')
    },
    {
      periodo: '2015',
      titulo: 'Consolidação e expansão',
      descricao: 'Em 2015, a Semana do Hip Hop de Bauru atingiu um novo patamar, consolidando-se como o maior evento gratuito de Hip Hop da América Latina. Com uma programação ampla e diversificada, o evento passou a atrair grande público e artistas de relevância nacional, ganhando prestígio e reconhecimento dentro e fora da cidade.',
      imagemPlaceholder: 'Show com Emicida, Renan Inquérito e Coruja BC1 durante a Semana do Hip Hop de Bauru',
      image: resolvePublicAsset('/images/semanahiphop/2015/renaninquerito_coruja_emicida.jpg')
    },
    {
      periodo: 'Anos\nseguintes',
      titulo: 'Referência nacional e fortalecimento da cena',
      descricao: 'Nos anos seguintes, a Semana do Hip Hop se tornou um modelo de organização e inspiração para outras cidades que passaram a realizar eventos semelhantes. Além de receber grandes nomes do rap nacional, o evento também se destaca por abrir espaço e fortalecer artistas em ascensão, especialmente do interior de ão Paulo.',
      imagemPlaceholder: 'Show com Síntese, Marechal e Negomax durante a Semana do Hip Hop de Bauru',
      image: resolvePublicAsset('/images/semanahiphop/2022/sintese_marchal_negomax.jpg')
    }
  ];

  const galeria = [
    {
      src: resolvePublicAsset('/images/semanahiphop/2015/emicida.jpg'),
      alt: 'Registro da Semana do Hip Hop de Bauru em atividade cultural'
    },
    {
      src: resolvePublicAsset('/images/semanahiphop/2016/batalha_breaking.jpg'),
      alt: 'Publico e artistas na Semana do Hip Hop de Bauru'
    },
    {
      src: resolvePublicAsset('/images/semanahiphop/2017/IMG_2773.jpg'),
      alt: 'Apresentacao da Semana do Hip Hop de Bauru'
    },
  ];

  useEffect(() => {
    const scrollTargetId = location.state?.scrollTargetId;

    if (!scrollTargetId) {
      return;
    }

    const scrollToSection = () => {
      const targetElement = document.getElementById(scrollTargetId);

      if (!targetElement) {
        return;
      }

      targetElement.scrollIntoView({ behavior: 'auto', block: 'center' });
      navigate(location.pathname, { replace: true, state: null });
    };

    const animationFrameId = requestAnimationFrame(scrollToSection);

    return () => cancelAnimationFrame(animationFrameId);
  }, [location.pathname, location.state, navigate]);

  return (
    <div className="week-page">
      <section className="week-hero">
        <Container>
          <Row>
            <Col>
              <ProjectBreadcrumbs
                items={[
                  { label: 'Projetos', to: '/projetos' },
                  { label: 'Semana do Hip Hop' }
                ]}
              />
            </Col>
          </Row>
          <Row className="align-items-center g-4">
            <Col lg={7}>
              <div className="week-hero-copy">
                <span className="week-kicker">O MAIOR EVENTO GRATUITO DE Hip Hop DA AMÉRICA LATINA</span>
                <h1>Semana do Hip Hop de Bauru</h1>
                <p>
                  Um dos eventos culturais mais relevantes de Bauru e uma referência nacional da cultura Hip Hop,
                  reunindo arte, formação, ocupação de espaços públicos e fortalecimento da juventude periférica.
                </p>
                <div className="week-tags">
                  <span>Bauru</span>
                  <span>Desde 2011</span>
                  <span>Programação gratuita</span>
                  <span>5 elementos do Hip Hop</span>
                </div>
              </div>
            </Col>
            <Col lg={5}>
              <div className="week-hero-visual" aria-label="Espaços reservados para fotos do projeto">
                <div className="week-photo-card week-photo-card-main">
                  <div className="week-photo-placeholder">
                    <img src={resolvePublicAsset('/images/semanahiphop/2024/logo.jpg')} alt="Logo da Semana do Hip Hop de Bauru" className="img-fluid" />
                  </div>
                </div>
              </div>
            </Col>
          </Row>
        </Container>
      </section>

      <section className="week-content-section">
        <Container>
          <Row className="g-4 align-items-start">
            <Col lg={8}>
              <div className="week-text-panel">
                <p>
                  A Semana do Hip Hop de Bauru é hoje reconhecida como um dos maiores eventos culturais da cidade, sendo referência na valorização da cultura urbana e na promoção de inclusão social por meio da arte. Sua trajetória é marcada por luta, organização coletiva e pelo protagonismo da própria comunidade.
                </p>

                <div className="week-timeline d-none d-md-grid" aria-label="Linha do tempo da história da Semana do Hip Hop de Bauru">
                  <div className="week-timeline-col week-timeline-col-left">
                    {historiaTimeline.filter((_, i) => i % 2 === 0).map((fase) => (
                      <div className="week-timeline-card" key={fase.periodo}>
                        <ImageDialogTrigger
                          src={fase.image}
                          alt={fase.imagemPlaceholder}
                          triggerClassName="week-timeline-card-img"
                          imageClassName="img-fluid"
                        />
                        <h3>{fase.titulo}</h3>
                        <p>{fase.descricao}</p>
                      </div>
                    ))}
                  </div>
                  <div className="d-none d-md-flex week-timeline-col week-timeline-col-center">
                    <div className="week-timeline-line" />
                    {historiaTimeline.map((fase) => (
                      <span className="week-timeline-period" key={fase.periodo}>{fase.periodo}</span>
                    ))}
                  </div>
                  <div className="week-timeline-col week-timeline-col-right">
                    {historiaTimeline.filter((_, i) => i % 2 !== 0).map((fase) => (
                      <div className="week-timeline-card" key={fase.periodo}>
                        <ImageDialogTrigger
                          src={fase.image}
                          alt={fase.imagemPlaceholder}
                          triggerClassName="week-timeline-card-img"
                          imageClassName="img-fluid"
                        />
                        <h3>{fase.titulo}</h3>
                        <p>{fase.descricao}</p>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="week-timeline-mobile d-block d-md-none" aria-label="Linha do tempo da história da Semana do Hip Hop de Bauru">
                  {historiaTimeline.map((fase, index) => (
                    <div className="week-timeline-mobile-item" key={fase.periodo}>
                      <div className="week-timeline-mobile-indicator">
                        <span className="week-timeline-mobile-dot">{fase.periodo}</span>
                        <div className={`week-timeline-mobile-line${index === 0 ? ' week-timeline-mobile-line--first' : ''}${index === historiaTimeline.length - 1 ? ' week-timeline-mobile-line--last' : ''}`} />
                      </div>
                      <div className="week-timeline-card">
                        <ImageDialogTrigger
                          src={fase.image}
                          alt={fase.imagemPlaceholder}
                          triggerClassName="week-timeline-card-img"
                          imageClassName="img-fluid"
                        />
                        <h3>{fase.titulo}</h3>
                        <p>{fase.descricao}</p>
                      </div>
                    </div>
                  ))}
                </div>

                <p>
                  Mais do que um evento, a Semana do Hip Hop de Bauru é um movimento cultural que promove os cinco elementos do Hip Hop — MC, DJ, breaking, grafite e conhecimento — e reafirma, a cada edição, seu compromisso com a cultura, a educação e a transformação social.
                </p>
              </div>
            </Col>
            <Col lg={4}>
              <div className="week-side-stack">
                <div className="week-highlight-card">
                  <span className="week-highlight-label">Destaques</span>
                  <ul>
                    <li>O maior evento gratuito de Hip Hop da América Latina.</li>
                    <li>Atividades em espaços públicos, escolas e centros culturais.</li>
                    <li>Centenas de artistas e ativistas reunidos ao longo das edições.</li>
                    <li>Público direto acumulado em centenas de milhares de pessoas.</li>
                    <li>Alcance indireto estimado em milhões.</li>
                  </ul>
                </div>
              </div>
              <div className="mt-4 week-highlight-card week-video-card">
                <span className="week-highlight-label">Vídeo</span>
                <div className="week-video-wrapper">
                  <iframe
                    src="https://www.youtube.com/embed/byyt0_uHenQ"
                    title="Vídeo da Semana do Hip Hop de Bauru"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  />
                </div>
                <a
                  href="https://www.youtube.com/@semanahiphopbauru"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="week-video-cta"
                >
                  Visite nosso canal no YouTube
                </a>
              </div>
            </Col>
          </Row>
        </Container>
      </section>

      <section className="week-gallery-section">
        <Container>
          <div className="week-section-heading">
            <span>Atmosfera do Projeto</span>
          </div>
          <Row className="g-4">
            {galeria.map((imagem) => (
              <Col md={4} key={imagem.src}>
                <ImageDialogTrigger
                  src={imagem.src}
                  alt={imagem.alt}
                  triggerClassName="week-gallery-card"
                  imageClassName="img-fluid"
                />
              </Col>
            ))}
          </Row>
        </Container>
      </section>

      <section className="week-editions-section" id="week-editions-section">
        <Container>
          <div className="week-section-heading">
            <span>Edições realizadas</span>
            <h4 className='mt-3'>Veja como foram as edições passadas</h4>
          </div>
          <Row className="g-4 week-editions-grid">
            {semanaEdicoes.map((edicao) => (
              <Col xl={3} lg={4} md={6} key={edicao.slug}>
                <Link
                  className="week-edition-link"
                  id={`edicao-card-${edicao.slug}`}
                  to={`/projetos/semana-do-hip-hop/${edicao.slug}`}
                >
                  <article className="week-edition-card">
                    <div className="week-edition-logo-placeholder">
                      <img src={edicao.image} alt={`Logo da ${edicao.titulo}`} className="img-fluid" />
                    </div>
                    <div className="week-edition-card-body">
                      <h3>{edicao.ano} - {edicao.titulo}</h3>
                      <span className="week-edition-cta">Ver mais</span>
                    </div>
                  </article>
                </Link>
              </Col>
            ))}
          </Row>
        </Container>
      </section>
    </div>
  );
}

export default SemanaHipHop;